class LockScroll {
	#count = $state(0);

	get locked(): boolean {
		return this.#count > 0;
	}

	/** Acquires a scroll lock. Multiple locks can be acquired, but only one is needed to lock scrolling. Using createScrollLock is recommended for component-scoped locking. */
	public acquire(): void {
		this.#count++;
	}

	/** Releases a scroll lock. Does nothing if no locks are held. */
	public release(): void {
		if (this.#count > 0) {
			this.#count--;
		}
	}
}

/** Global scroll lock with reference counting — locked while any consumer holds a lock. */
export const lockScroll = new LockScroll();

/**
 * Creates a component-scoped scroll lock handle. Set `.locked = true/false` to acquire/release.
 * The lock is automatically released when the calling component is destroyed.
 *
 * @example
 * const scrollLock = createScrollLock();
 * $effect(() => { scrollLock.locked = show; });
 */
export function createScrollLock(): { locked: boolean } {
	let held = false;

	function acquire(): void {
		if (!held) {
			held = true;
			lockScroll.acquire();
		}
	}

	function release(): void {
		if (held) {
			held = false;
			lockScroll.release();
		}
	}

	$effect(() => {
		return (): void => {
			release();
		};
	});

	return {
		get locked(): boolean {
			return held;
		},
		set locked(value: boolean) {
			if (value) {
				acquire();
			} else {
				release();
			}
		},
	};
}
